import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Layers, ArrowRight, ShieldCheck, Activity, Image as ImageIcon, ScanSearch, Receipt, FileText, Database, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";


type Capability = {
  key: string;
  name: string;
  title: string;
  line: string;
  Icon: LucideIcon;
};

const CAPABILITIES: Capability[] = [
  {
    key: "build",
    name: "Build",
    title: "Package AI capabilities.",
    line: "Build, buy, or validate.",
    Icon: Layers,
  },
  {
    key: "deploy",
    name: "Deploy",
    title: "Into existing workflows.",
    line: "Where teams already work.",
    Icon: ArrowRight,
  },
  {
    key: "govern",
    name: "Govern",
    title: "Monitor every workflow.",
    line: "One evidence trail.",
    Icon: ShieldCheck,
  },
];

const INPUT_CHIPS: { label: string; Icon: LucideIcon }[] = [
  { label: "EHR", Icon: Activity },
  { label: "PACS", Icon: ImageIcon },
  { label: "REPORTING", Icon: ScanSearch },
  { label: "CLAIMS", Icon: Receipt },
  { label: "DOCS", Icon: FileText },
  { label: "DATA", Icon: Database },
];

const STEP_MS = 4000;

const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
};

const FoundationDiagramAct3 = () => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const cycling = isDesktop && !reduceMotion;

  const [active, setActive] = useState(0);
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    if (!cycling || pinned) return;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % CAPABILITIES.length);
    }, STEP_MS);
    return () => clearInterval(id);
  }, [cycling, pinned]);

  // ---- Converging lines ----
  const inputLayerRef = useRef<HTMLDivElement>(null);
  const chipsWrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const chipRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const lineRefs = useRef<Array<SVGPathElement | null>>([]);
  const pulseRefs = useRef<Array<SVGPathElement | null>>([]);

  useLayoutEffect(() => {
    const container = inputLayerRef.current;
    const svg = svgRef.current;
    if (!container || !svg) return;

    const alignLines = () => {
      const rect = container.getBoundingClientRect();
      if (rect.width === 0) return;
      svg.setAttribute("viewBox", `0 0 ${rect.width} ${rect.height}`);
      const startY = 0;
      const n = chipRefs.current.length;
      chipRefs.current.forEach((chip, idx) => {
        if (!chip) return;
        const cr = chip.getBoundingClientRect();
        const ex = cr.left + cr.width / 2 - rect.left;
        const ey = cr.top - rect.top + cr.height / 2;
        // Spread start points horizontally across the foundation's bottom edge
        const startX = rect.width * ((idx + 1) / (n + 1));
        const midY = startY + (ey - startY) * 0.6;
        const d = `M ${startX} ${startY} Q ${ex} ${midY} ${ex} ${ey}`;
        lineRefs.current[idx]?.setAttribute("d", d);
        pulseRefs.current[idx]?.setAttribute("d", d);
      });
    };

    alignLines();
    const ro = new ResizeObserver(alignLines);
    ro.observe(container);
    window.addEventListener("resize", alignLines);
    const t1 = window.setTimeout(alignLines, 50);
    const t2 = window.setTimeout(alignLines, 300);
    if (document.fonts?.ready) document.fonts.ready.then(alignLines);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", alignLines);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  return (
    <div>
      <div>
        <style>{`
          @keyframes fd3-flow {
            from { stroke-dashoffset: 0; }
            to { stroke-dashoffset: 84; }
          }
          @keyframes fd3-pulse {
            0% { transform: scale(0.7); opacity: 0.9; }
            100% { transform: scale(1.8); opacity: 0; }
          }
          @keyframes fd3-fill {
            from { transform: scaleX(0); }
            to { transform: scaleX(1); }
          }
          .fd3-pulse { animation: fd3-flow 2.4s linear infinite; }
          .fd3-dot::after {
            content: '';
            position: absolute;
            inset: -5px;
            border-radius: 9999px;
            background: hsl(var(--primary) / 0.2);
            animation: fd3-pulse 2s ease-out infinite;
          }
          .fd3-accent-fill {
            animation: fd3-fill ${STEP_MS}ms linear forwards;
            transform-origin: left;
          }
          @media (prefers-reduced-motion: reduce) {
            .fd3-pulse { animation: none; opacity: 0.35; }
            .fd3-dot::after { animation: none; }
            .fd3-accent-fill { animation: none; transform: scaleX(1); }
          }
        `}</style>

        {/* Foundation card */}
        <div className="relative rounded-2xl border border-border bg-background overflow-hidden shadow-sm">
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(80% 50% at 50% 100%, hsl(var(--primary) / 0.06), transparent 70%)",
            }}
          />

          {/* Header */}
          <div className="relative flex items-center justify-between gap-3 px-4 md:px-7 py-4 md:py-5 border-b border-border overflow-hidden">
            <span className="flex items-center gap-2 md:gap-3 text-[10px] md:text-[13px] font-semibold tracking-[0.06em] md:tracking-[0.1em] uppercase text-primary min-w-0">
              <span className="fd3-dot relative inline-block w-2 h-2 rounded-full bg-primary shrink-0" />
              <span className="normal-case lowercase">deepcOS</span> · <span className="md:hidden">AI INFRASTRUCTURE</span><span className="hidden md:inline">CLINICAL AI INFRASTRUCTURE</span>
            </span>
            <span className="hidden md:flex gap-3.5 text-[11px] font-medium tracking-[0.14em] uppercase text-muted-foreground">
              <span>Build</span>
              <span className="relative before:content-['·'] before:absolute before:-left-2 before:text-primary/60">
                Deploy
              </span>
              <span className="relative before:content-['·'] before:absolute before:-left-2 before:text-primary/60">
                Govern
              </span>
            </span>
          </div>

          {/* Capabilities */}
          <div className="relative grid grid-cols-1 md:grid-cols-3">
            {CAPABILITIES.map((cap, idx) => {
              const isActive = cycling ? idx === active : true;
              const showAccent = cycling && isActive;
              const accentFull = pinned && isActive;
              return (
                <div
                  key={cap.key}
                  onMouseEnter={() => {
                    if (!cycling) return;
                    setPinned(true);
                    setActive(idx);
                  }}
                  onMouseLeave={() => {
                    if (!cycling) return;
                    setPinned(false);
                  }}
                  className={cn(
                    "relative px-7 py-8 md:py-9 cursor-pointer border-b md:border-b-0 md:border-r border-border last:border-r-0 last:border-b-0 transition-[opacity,background] duration-500",
                    cycling && !isActive && "opacity-40",
                    cycling && isActive && "bg-primary/[0.04]"
                  )}
                >
                  {/* Top progress bar: always-visible track + animated fill on active */}
                  <span
                    aria-hidden
                    className="hidden md:block absolute top-0 left-0 h-0.5 w-full bg-primary/15 overflow-hidden"
                  >
                    {cycling && isActive && (
                      <span
                        key={`${idx}-${active}-${pinned ? "pin" : "run"}`}
                        className={cn(
                          "block absolute inset-0 bg-primary origin-left",
                          pinned ? "scale-x-100" : "fd3-accent-fill"
                        )}
                      />
                    )}
                  </span>

                  <div
                    className={cn(
                      "w-10 h-10 rounded-[11px] border flex items-center justify-center mb-4 transition-colors duration-400",
                      isActive && cycling
                        ? "bg-primary border-primary text-primary-foreground"
                        : "bg-primary/[0.06] border-primary/15 text-primary"
                    )}
                  >
                    <cap.Icon size={20} strokeWidth={1.5} />
                  </div>
                  <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-primary mb-2.5">
                    {cap.name}
                  </p>
                  <p className="text-[17px] md:text-[18px] font-medium leading-snug tracking-[-0.01em] text-foreground mb-2">
                    {cap.title}
                  </p>
                  <p className="text-[13px] md:text-[14px] leading-relaxed text-muted-foreground">
                    {cap.line}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Input layer with chips + diverging lines (below foundation) */}
        <div ref={inputLayerRef} className="relative hidden md:block">

          <div className="h-14" aria-hidden />
          <div
            ref={chipsWrapRef}
            className="relative z-[2] flex flex-nowrap justify-between items-center gap-x-2 md:gap-x-3 lg:gap-x-4 px-1 md:px-2 lg:px-10"
            aria-label="AI input sources"
          >
            {INPUT_CHIPS.map(({ label, Icon }, i) => (
              <span
                key={label}
                ref={(el) => (chipRefs.current[i] = el)}
                className="relative z-10 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-medium bg-background text-foreground/80 border border-border whitespace-nowrap"
              >
                <Icon size={14} strokeWidth={1.75} className="hidden md:inline-block" aria-hidden />
                {label}
              </span>
            ))}
          </div>
          <svg
            ref={svgRef}
            aria-hidden
            preserveAspectRatio="none"
            className="absolute inset-0 w-full h-full z-[1] pointer-events-none overflow-visible"
          >
            {INPUT_CHIPS.map((_, i) => (
              <path
                key={`l-${i}`}
                ref={(el) => (lineRefs.current[i] = el)}
                fill="none"
                stroke="hsl(var(--primary) / 0.4)"
                strokeWidth={2}
              />
            ))}
            {INPUT_CHIPS.map((_, i) => (
              <path
                key={`p-${i}`}
                ref={(el) => (pulseRefs.current[i] = el)}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth={3}
                strokeLinecap="round"
                strokeDasharray="6 78"
                className="fd3-pulse"
                style={{ animationDelay: `${i * 0.4}s` }}
              />
            ))}
          </svg>
        </div>

        {/* Eyebrow */}
        <p className="hidden md:block text-center text-[10px] font-semibold tracking-[0.16em] uppercase text-muted-foreground/70 mt-14">
          CONNECTED SYSTEMS
        </p>
      </div>
    </div>
  );
};

export default FoundationDiagramAct3;
